<template>
  <div>
    <v-autocomplete
      v-model="localValue"
      :items="sortedTagOptions"
      :loading="isLoading"
      item-text="label"
      item-value="id"
      :label="$t('STOCK.PRODUCTS.TAGS')"
      multiple
      chips
      small-chips
      clearable
      :search-input.sync="search"
      :error-messages="errorMessages"
      :disabled="!canSuggestTags"
      :hint="tagHint"
      persistent-hint
      :filter="filterTagOptions"
      @change="emitChange"
      @update:search-input="handleSearch"
    >
      <template v-slot:append-outer>
        <v-btn icon color="primary" :disabled="!canSuggestTags" @click="showCreateDialog = true">
          <v-icon>mdi-plus</v-icon>
        </v-btn>
      </template>
      <template v-slot:item="{ item }">
        <v-list-item-content>
          <v-list-item-title>{{ item.label }}</v-list-item-title>
          <v-list-item-subtitle>
            {{ rankLabel(item.rank_group) }}
          </v-list-item-subtitle>
        </v-list-item-content>
      </template>
    </v-autocomplete>

    <v-dialog v-model="showCreateDialog" max-width="460">
      <v-card>
        <v-card-title>{{ $t("STOCK.PRODUCTS.CREATE_TAG") }}</v-card-title>
        <v-card-text>
          <v-form ref="tagForm" v-model="validTagForm" @submit.prevent>
            <v-text-field
              ref="newTagNameInput"
              v-model="newTag.name"
              :label="$t('STOCK.PRODUCTS.TAG_NAME') + '*'"
              :rules="[validations.required()]"
              :error-messages="validationErrors.name || []"
            ></v-text-field>
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="closeCreateDialog">
            {{ $t("GENERAL.LABELS.CLOSE_BUTTON") }}
          </v-btn>
          <v-btn text color="primary" :loading="isCreating" @click="createTag">
            {{ $t("GENERAL.LABELS.CREATE_BUTTON") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { debounce } from "lodash";
import validations from "@/views/partials/form/Validations.vue";
import { SHOW_SNACK } from "@/store/snack.module";
import {
  CREATE_STOCK_TAG,
  GET_STOCK_TAG_OPTIONS,
} from "@/store/stock/stockProducts.module";

export default {
  name: "StockTagPicker",
  props: {
    value: {
      type: Array,
      default: () => [],
    },
    categoryId: {
      default: null,
    },
    displayName: {
      type: String,
      default: "",
    },
    initialTags: {
      type: Array,
      default: () => [],
    },
    errorMessages: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      validations,
      localValue: [...this.value],
      tagOptions: [...this.initialTags],
      search: "",
      isLoading: false,
      isCreating: false,
      showCreateDialog: false,
      validTagForm: false,
      validationErrors: {},
      newTag: {
        name: "",
      },
    };
  },
  computed: {
    canSuggestTags() {
      return !!this.categoryId && !!(this.displayName || "").trim();
    },
    tagHint() {
      if (this.canSuggestTags) {
        return "";
      }
      return this.$t("STOCK.PRODUCTS.TAG_HINT_REQUIRES_NAME_CATEGORY");
    },
    sortedTagOptions() {
      return this.sortTagOptions(this.tagOptions);
    },
  },
  watch: {
    value(value) {
      this.localValue = [...value];
    },
    initialTags: {
      handler() {
        this.loadTags(this.search || "");
      },
      deep: true,
    },
    categoryId() {
      this.loadTags("");
    },
    displayName() {
      this.loadTags(this.search || "");
    },
    showCreateDialog(value) {
      if (value) {
        this.$nextTick(() => {
          setTimeout(() => this.$refs.newTagNameInput?.focus(), 100);
        });
      }
    },
  },
  mounted() {
    this.loadTags("");
  },
  methods: {
    handleSearch: debounce(function (search) {
      this.loadTags(search || "");
    }, 350),
    loadTags(search) {
      if (!this.canSuggestTags) {
        this.tagOptions = this.buildTagOptions([], this.initialTags);
        return Promise.resolve();
      }

      this.isLoading = true;
      return this.$store
        .dispatch(GET_STOCK_TAG_OPTIONS, {
          search,
          stock_product_category_id: this.categoryId,
          display_name: (this.displayName || "").trim(),
        })
        .then((response) => {
          this.tagOptions = this.buildTagOptions(response.data || [], this.initialTags);
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
    sortTagOptions(tags) {
      return [...(tags || [])].sort((left, right) => {
        const leftRank = left.rank_group === "same_product" ? 0 : 1;
        const rightRank = right.rank_group === "same_product" ? 0 : 1;

        if (leftRank !== rightRank) {
          return leftRank - rightRank;
        }

        return (left.label || left.name || "").localeCompare(right.label || right.name || "");
      });
    },
    buildTagOptions(apiTags, initialTags) {
      const byId = {};
      const ordered = [];

      (apiTags || []).forEach((tag) => {
        if (!tag?.id) {
          return;
        }

        byId[tag.id] = tag;
        ordered.push(tag);
      });

      (initialTags || []).forEach((tag) => {
        if (!tag?.id || byId[tag.id]) {
          return;
        }

        const mergedTag = {
          ...tag,
          rank_group: tag.rank_group || "same_product",
        };
        byId[tag.id] = mergedTag;
        ordered.push(mergedTag);
      });

      return ordered;
    },
    filterTagOptions(item, queryText) {
      if (!queryText) {
        return true;
      }

      const label = (item.label || item.name || "").toLowerCase();
      return label.includes(queryText.toLowerCase());
    },
    emitChange() {
      this.$emit("input", this.localValue);
      this.$emit("change");
    },
    closeCreateDialog() {
      this.showCreateDialog = false;
      this.validationErrors = {};
      this.newTag = {
        name: "",
      };
    },
    createTag() {
      this.$refs.tagForm.validate();
      if (!this.validTagForm) {
        return;
      }

      this.validationErrors = {};
      this.isCreating = true;

      this.$store
        .dispatch(CREATE_STOCK_TAG, this.newTag)
        .then((tag) => {
          this.tagOptions = this.buildTagOptions([{ ...tag, rank_group: "same_product" }], this.initialTags);
          this.localValue = [...this.localValue, tag.id];
          this.emitChange();
          this.closeCreateDialog();
        })
        .catch((error) => {
          this.validationErrors = error?.errors || {};
          this.$store.dispatch(SHOW_SNACK, {
            message: error?.message || this.$t("GENERAL.UNEXPECTED_ERROR"),
            type: "error",
          });
        })
        .finally(() => {
          this.isCreating = false;
        });
    },
    rankLabel(rankGroup) {
      if (rankGroup === "same_product") {
        return this.$t("STOCK.PRODUCTS.TAG_RANK.SAME_PRODUCT");
      }

      return this.$t("STOCK.PRODUCTS.TAG_RANK.OTHER");
    },
  },
};
</script>
